/**
 * ### INQUIRY_INTEREST_VALUES
 *
 * Every field of interest the inquiry form offers, in display order.
 * Each value is the stable machine key used as the form option value, the
 * `?interest=` query key, and the analytics parameter; it never changes when a label is reworded.
 */
export const INQUIRY_INTEREST_VALUES = [
  'audio-review',
  'process-consult',
  'pdm-consult',
  'poc-terms',
  'plc-mes',
  'safety-consult',
  'partnership',
  'other',
] as const;

/**
 * ### InquiryInterest
 *
 * Machine key of one field of interest.
 */
export type InquiryInterest = (typeof INQUIRY_INTEREST_VALUES)[number];

/**
 * ### INQUIRY_INTEREST_LABEL_KEYS
 *
 * Korean label of each interest.
 * The label plays two roles: it is the translation key handed to `t()`, and it is the value
 * persisted in `add_data.interest` so the admin board renders it without a mapping step.
 */
export const INQUIRY_INTEREST_LABEL_KEYS = {
  'audio-review': '보유 음원 검토 요청',
  'process-consult': '공정 환경 상담',
  'pdm-consult': '예지보전 적용 상담',
  'poc-terms': 'PoC 조건 확인',
  'plc-mes': 'PLC·MES 연동 검토',
  'safety-consult': 'Safety 도입 상담',
  partnership: '파트너십',
  other: '기타',
} as const satisfies Record<InquiryInterest, string>;

/**
 * ### InquiryInterestRecordValue
 *
 * Value stored in `add_data.interest` for a submission made with the current option set.
 * Rows written before this set existed carry older labels that are outside this type.
 */
export type InquiryInterestRecordValue =
  (typeof INQUIRY_INTEREST_LABEL_KEYS)[InquiryInterest];

/**
 * Query keys that older call-to-action links still send, mapped to the interest they now mean.
 * `demo` was the only key published before the interest set was standardized.
 */
const LEGACY_INTEREST_QUERY_ALIASES = {
  demo: 'poc-terms',
} as const satisfies Record<string, InquiryInterest>;

/**
 * ### isInquiryInterest
 *
 * Reports whether a value is one of the current interest machine keys.
 * Legacy query aliases such as `demo` are not interests; see {@link parseInquiryInterestQuery}.
 *
 * @param value Value of unverified shape, typically a form value or query parameter
 * @returns Whether the value can be treated as InquiryInterest
 */
export function isInquiryInterest(value: unknown): value is InquiryInterest {
  return INQUIRY_INTEREST_VALUES.some((interest) => interest === value);
}

/**
 * ### parseInquiryInterestQuery
 *
 * Resolves the `?interest=` query parameter to an interest, honouring legacy aliases.
 * Anything that is not a single recognised string, including a repeated parameter,
 * resolves to `undefined` so the form starts with no interest selected.
 *
 * @param value Raw query value as exposed by the router
 * @returns The matching interest, or `undefined` when the query names none
 */
export function parseInquiryInterestQuery(
  value: unknown,
): InquiryInterest | undefined {
  if (typeof value !== 'string') return undefined;

  const aliasedInterest = Object.entries(LEGACY_INTEREST_QUERY_ALIASES).find(
    ([alias]) => alias === value,
  )?.[1];
  if (aliasedInterest) return aliasedInterest;

  return isInquiryInterest(value) ? value : undefined;
}

/**
 * ### inquiryInterestLabelKey
 *
 * Returns the translation key that renders the label of an interest.
 *
 * @param interest Interest to label
 * @returns The `common` namespace key to pass to `t()`
 */
export function inquiryInterestLabelKey(interest: InquiryInterest): string {
  return INQUIRY_INTEREST_LABEL_KEYS[interest];
}

/**
 * ### inquiryInterestRecordValue
 *
 * Returns the value to persist in `add_data.interest` for an interest.
 * The admin board displays `add_data` fields as raw text, so the persisted value is the
 * Korean label rather than the machine key; analytics keep the machine key instead.
 *
 * @param interest Interest the visitor selected
 * @returns The label to store with the inquiry
 */
export function inquiryInterestRecordValue(
  interest: InquiryInterest,
): InquiryInterestRecordValue {
  return INQUIRY_INTEREST_LABEL_KEYS[interest];
}

/**
 * ### isInquiryInterestRecordValue
 *
 * Reports whether a value is a persisted interest produced by the current option set.
 * Labels from retired option sets are rejected so a new submission cannot reintroduce them.
 *
 * @param value Value of unverified shape, typically `add_data.interest` from a request body
 * @returns Whether the value can be treated as InquiryInterestRecordValue
 */
export function isInquiryInterestRecordValue(
  value: unknown,
): value is InquiryInterestRecordValue {
  return INQUIRY_INTEREST_VALUES.some(
    (interest) => INQUIRY_INTEREST_LABEL_KEYS[interest] === value,
  );
}
